import type {
  AttributeDefinition,
  AttributeOption,
  AttributeValue,
  Garment,
  SessionView,
} from '@fitting-room/contracts'
import { text } from './text'

/**
 * The client's entire understanding of "what a garment is like" is this file:
 * look a key up in `tenant.attributes`, ask the definition for its options and
 * labels, and render whatever comes back. There is no list of known attribute
 * keys anywhere in the app, which is the point.
 */
export type AttributeIndex = ReadonlyMap<string, AttributeDefinition>

export function indexAttributes(defs: readonly AttributeDefinition[]): AttributeIndex {
  return new Map(defs.map((def) => [def.key, def]))
}

export function sortedOptions(def: AttributeDefinition): readonly AttributeOption[] {
  return [...def.options].sort((a, b) => a.position - b.position)
}

export function optionOf(def: AttributeDefinition | undefined, value: string): AttributeOption | undefined {
  return def?.options.find((o) => o.value === value)
}

export function optionLabel(def: AttributeDefinition | undefined, value: string, locale?: string): string {
  const option = optionOf(def, value)
  return option ? text(option.label, locale) : value
}

/** Attribute assignments flattened to a bag, so a card can look values up by key. */
export function attributeBag(garment: Garment): ReadonlyMap<string, AttributeValue> {
  return new Map(garment.attributes.map((a) => [a.key, a.value]))
}

/** A human string for one attribute value, resolved entirely through the definition. */
export function formatAttributeValue(
  def: AttributeDefinition | undefined,
  value: AttributeValue | undefined,
  locale?: string,
): string {
  if (value === undefined || value === null) return ''
  if (Array.isArray(value)) {
    return value.map((v) => optionLabel(def, v, locale)).join(' · ')
  }
  if (typeof value === 'boolean') return value ? '✓' : '—'
  if (typeof value === 'number') return def?.unit ? `${value} ${def.unit}` : String(value)
  return optionLabel(def, value, locale)
}

/** Attributes the tenant marked as customer-facing, in their declared order. */
export function customerVisibleAttributes(defs: readonly AttributeDefinition[]): readonly AttributeDefinition[] {
  return [...defs].filter((d) => d.customerVisible).sort((a, b) => a.position - b.position)
}

export function slotLabel(view: SessionView, slotKey: string, locale?: string): string {
  const slot = view.flow.slots.find((s) => s.key === slotKey)
  return slot ? text(slot.label, locale) : slotKey
}

export function slotDefinition(view: SessionView, slotKey: string) {
  return view.flow.slots.find((s) => s.key === slotKey)
}

export function slotState(view: SessionView, slotKey: string) {
  return view.slotStates.find((s) => s.key === slotKey)
}
